import { useCallback, useEffect, useMemo, useRef, type ReactElement } from "react";
import { basicSetup, EditorView } from "codemirror";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import { Decoration, keymap, placeholder, type DecorationSet } from "@codemirror/view";
import {
  defaultKeymap,
  historyKeymap,
  indentWithTab,
  toggleLineComment,
} from "@codemirror/commands";
import { bracketMatching, syntaxHighlighting } from "@codemirror/language";
import { gotoLine, openSearchPanel, searchKeymap } from "@codemirror/search";
import { ArrowLeft, CircleAlert, ListOrdered, Replace, Search } from "lucide-react";
import { Button } from "../../ui/index.js";
import { useTr, type Entry } from "../../i18n/context.js";
import { lunascript, lunascriptHighlightStyle } from "./lunascript-language.js";

const S = {
  source: { zh: "剧本源码", en: "Script source" },
  search: { zh: "搜索", en: "Search" },
  replace: { zh: "替换", en: "Replace" },
  goLine: { zh: "跳到行", en: "Go to line" },
  back: { zh: "返回卡片", en: "Back to Cards" },
  problems: { zh: "问题 {count}", en: "Problems {count}" },
  line: { zh: "第 {line} 行", en: "Line {line}" },
  saved: { zh: "已保存", en: "Saved" },
  saving: { zh: "保存中…", en: "Saving…" },
  unsaved: { zh: "待保存", en: "Unsaved" },
} satisfies Record<string, Entry>;

export interface CodeDiagnostic {
  readonly line: number;
  readonly message: string;
  readonly severity?: "error" | "warning";
}

export type CodeSaveState = "saved" | "saving" | "unsaved";

export interface CodeEditorProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly readOnly?: boolean;
  readonly initialLine?: number | null;
  readonly onInitialLineApplied?: () => void;
  readonly diagnostics?: readonly CodeDiagnostic[];
  readonly saveState?: CodeSaveState;
  readonly onBackToCards?: () => void;
}

export function diagnosticLineFromMessage(message: string): number | null {
  const named = message.match(/\bline\s+(\d+)\b/i)?.[1];
  if (named) return Number(named);
  const located = message.match(/(?:^|\s|[/\\])[^\s:]+\.(?:ls|lunascript):(\d+)(?::\d+)?\b/i)?.[1];
  return located ? Number(located) : null;
}

function diagnosticExtension(state: EditorState, diagnostics: readonly CodeDiagnostic[]): Extension {
  const ranges = diagnostics.flatMap((diagnostic) => {
    if (!Number.isFinite(diagnostic.line) || state.doc.lines === 0) return [];
    const line = state.doc.line(Math.min(state.doc.lines, Math.max(1, diagnostic.line)));
    return [Decoration.line({
      class: diagnostic.severity === "warning" ? "cm-lunaria-warning" : "cm-lunaria-error",
      attributes: { title: diagnostic.message },
    }).range(line.from)];
  });
  const decorations: DecorationSet = Decoration.set(ranges, true);
  return EditorView.decorations.of(decorations);
}

function lineAt(view: EditorView, line: number): number {
  return view.state.doc.line(Math.min(view.state.doc.lines, Math.max(1, line))).from;
}

export function CodeEditor({
  value,
  onChange,
  readOnly = false,
  initialLine,
  onInitialLineApplied,
  diagnostics = [],
  saveState = "saved",
  onBackToCards,
}: CodeEditorProps): ReactElement {
  const tr = useTr();
  const mountRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const externalUpdateRef = useRef(false);
  const readOnlyCompartmentRef = useRef(new Compartment());
  const diagnosticsCompartmentRef = useRef(new Compartment());
  onChangeRef.current = onChange;

  useEffect(() => {
    const parent = mountRef.current;
    if (!parent) return undefined;
    const state = EditorState.create({
      doc: value,
      extensions: [
        basicSetup,
        lunascript,
        syntaxHighlighting(lunascriptHighlightStyle),
        bracketMatching(),
        EditorView.lineWrapping,
        placeholder('@episode main:01 "Chapter one" { … }'),
        keymap.of([
          { key: "Mod-/", run: toggleLineComment },
          { key: "Mod-h", run: openSearchPanel },
          { key: "Mod-g", run: gotoLine },
          indentWithTab,
          ...searchKeymap,
          ...historyKeymap,
          ...defaultKeymap,
        ]),
        EditorView.updateListener.of((update) => {
          if (!update.docChanged || externalUpdateRef.current) return;
          onChangeRef.current(update.state.doc.toString());
        }),
        readOnlyCompartmentRef.current.of([
          EditorState.readOnly.of(readOnly),
          EditorView.editable.of(!readOnly),
        ]),
        diagnosticsCompartmentRef.current.of(diagnosticExtension(EditorState.create({ doc: value }), diagnostics)),
      ],
    });
    const view = new EditorView({ state, parent });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // The editor is intentionally created once; controlled changes are synced
    // by the effects below without throwing away history or the search panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || view.state.doc.toString() === value) return;
    externalUpdateRef.current = true;
    view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: value } });
    externalUpdateRef.current = false;
  }, [value]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: readOnlyCompartmentRef.current.reconfigure([
        EditorState.readOnly.of(readOnly),
        EditorView.editable.of(!readOnly),
      ]),
    });
  }, [readOnly]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: diagnosticsCompartmentRef.current.reconfigure(diagnosticExtension(view.state, diagnostics)),
    });
  }, [diagnostics]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || initialLine == null) return;
    const anchor = lineAt(view, initialLine);
    view.dispatch({
      selection: { anchor },
      effects: EditorView.scrollIntoView(anchor, { y: "center" }),
    });
    view.focus();
    onInitialLineApplied?.();
  }, [initialLine, onInitialLineApplied]);

  const run = useCallback((command: (view: EditorView) => boolean) => {
    const view = viewRef.current;
    if (!view) return;
    command(view);
    view.focus();
  }, []);

  const openReplace = useCallback(() => {
    run(openSearchPanel);
    window.requestAnimationFrame(() => {
      const replaceInput = mountRef.current?.querySelector<HTMLInputElement>('.cm-search input[name="replace"]');
      replaceInput?.focus();
    });
  }, [run]);

  const saveLabel = useMemo(() => tr(S[saveState]), [saveState, tr]);

  return (
    <section
      className="lc-code-editor"
      data-code-editor="codemirror"
      data-read-only={readOnly || undefined}
      aria-label={tr(S.source)}
    >
      <header className="lc-code-editor__toolbar">
        <span className="lc-code-editor__save" data-state={saveState}>{saveLabel}</span>
        <span className="lc-code-editor__problem-count">
          <CircleAlert size={14} aria-hidden="true" />{tr(S.problems, { count: diagnostics.length })}
        </span>
        <span className="lc-code-editor__toolbar-spacer" />
        <Button size="sm" variant="ghost" leadingIcon={<Search size={14} />} onClick={() => run(openSearchPanel)}>{tr(S.search)}</Button>
        <Button size="sm" variant="ghost" leadingIcon={<Replace size={14} />} onClick={openReplace}>{tr(S.replace)}</Button>
        <Button size="sm" variant="ghost" leadingIcon={<ListOrdered size={14} />} onClick={() => run(gotoLine)}>{tr(S.goLine)}</Button>
        {onBackToCards ? <Button size="sm" variant="secondary" leadingIcon={<ArrowLeft size={14} />} onClick={onBackToCards}>{tr(S.back)}</Button> : null}
      </header>

      <div ref={mountRef} className="lc-code-editor__mount" />

      <div className="lc-code-editor__problems" role="status" aria-live="polite">
        <strong>{tr(S.problems, { count: diagnostics.length })}</strong>
        {diagnostics.length > 0 ? (
          <ul>
            {diagnostics.map((diagnostic, index) => (
              <li key={`${diagnostic.line}:${diagnostic.message}:${index}`}>
                <button type="button" onClick={() => {
                  const view = viewRef.current;
                  if (!view) return;
                  const anchor = lineAt(view, diagnostic.line);
                  view.dispatch({ selection: { anchor }, effects: EditorView.scrollIntoView(anchor, { y: "center" }) });
                  view.focus();
                }}>
                  <span>{tr(S.line, { line: diagnostic.line })}</span>
                  <span>{diagnostic.message}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
